import React, {useState} from 'react';
import {useDispatch} from 'react-redux';

import Utility from '../../../../util/utilityfunctions';
import {currentResource, labels} from '../../../../util/utils';

import {updateReconciliationAccount} from '../../../../actions/Reconciliations';
import selectMappedComponentAccounts from '../../../../reducers/Reconciliations/selectors/selectMappedComponentAccounts';
import selectProjectAndEngIds from '../../../../reducers/Reconciliations/selectors/selectProjectAndEngIds';
import selectRelatableComponentItems from '../../../../reducers/Reconciliations/selectors/selectRelatableComponentItems';

import {SELECTEDACCOUNTS, CURRENTSELECTION} from './utils/constants';

import OutputText from '@ey/canvascoreservices/OutputText';

import useForm from '../../../CoreWrappers/Form/useForm';
import Modal from '../../../CoreWrappers/Modal';
import RelateAccountsTable from '../../RelateAccountsTable';
import StyledRelateAccountsModal from './StyledRelateAccountsModal';
import StyledLabelKeyValues from './StyledLabelKeyValues';
import StyledLabel from './StyledLabel';

const RelateAccountsModal = ({primaryAccount = null, show = false, setShow = null}) => {
	const [isLoading, setIsLoading] = useState([]);

	const apiConfig = selectProjectAndEngIds();
	const componentAccounts = selectRelatableComponentItems('account');

	const dispatch = useDispatch();

	const onHide = () => setShow(false);

	const handleSetIsLoading = (id, add = false) => {
		let newValue = [];
		if (add) newValue = [...isLoading, id];
		else isLoading.filter((oldId) => oldId !== id);
		setIsLoading([...newValue]);
	};

	const initialValues = {
		[SELECTEDACCOUNTS]: selectMappedComponentAccounts(primaryAccount.accountId, true) ?? [],
		[CURRENTSELECTION]: []
	};

	const schema = {
		[SELECTEDACCOUNTS]: {required: false},
		[CURRENTSELECTION]: {required: false}
	};

	const {values, errors, handleChange, handleSubmit, handleReset, isFormValid} = useForm({
		initialValues,
		schema,
		onChange: (values, revertChange) => {
			let selectedAccount = values[CURRENTSELECTION];
			let componentAccountsToAdd = [];
			let componentAccountsToRemove = [];
			if (selectedAccount?.isAdded) {
				componentAccountsToAdd = selectedAccount.id;
			} else {
				componentAccountsToRemove = selectedAccount.id;
			}

			const payload = {
				RelatedComponentAccounts: {componentAccountsToAdd, componentAccountsToRemove}
			};
			setIsLoading([...componentAccountsToAdd, ...componentAccountsToRemove]);
			dispatch(updateReconciliationAccount(primaryAccount.accountId, payload, apiConfig))
				.then(() => handleSetIsLoading(selectedAccount.id[0]))
				.catch(() => {
					handleSetIsLoading(selectedAccount.id[0]);
					revertChange();
				});
		},
		labels: currentResource.formLabels
	});

	const renderAccountName = () => (
		<StyledLabelKeyValues>
			<OutputText body1 light>
				{labels.relateAccountName}
			</OutputText>
			<OutputText h6 ellipsis multilineEllipsis={2} title={primaryAccount.accountName}>
				{primaryAccount.accountName}
			</OutputText>
		</StyledLabelKeyValues>
	);

	const renderPspIndex = () => {
		const isOne = primaryAccount.pspIndexes?.length === 1;
		const isEmpty = !(primaryAccount.pspIndexes?.length > 0);
		let value = isOne
			? primaryAccount.pspIndexes[0]?.pspIndexTitle
			: primaryAccount.pspIndexes.map(({pspIndexTitle}) => pspIndexTitle).join(', ');
		if (isEmpty) value = '-';

		return (
			<StyledLabelKeyValues>
				<OutputText light>{labels.relateAccountpspIndexes}</OutputText>
				<OutputText ellipsis title={value} multilineEllipsis={2}>
					{value}
				</OutputText>
			</StyledLabelKeyValues>
		);
	};

	const renderTable = () => {
		const id = SELECTEDACCOUNTS;

		const onSelect = (selectedId) => {
			let currentValues = values[id];
			if (currentValues?.includes(selectedId)) {
				currentValues = currentValues.filter((f) => f !== selectedId);
				handleChange(id, [...currentValues], {[CURRENTSELECTION]: {id: [selectedId], isAdded: false}});
			} else {
				handleChange(id, [...currentValues, selectedId], {[CURRENTSELECTION]: {id: [selectedId], isAdded: true}});
			}
		};

		const mapComponent = (componentAccount) => ({
			...componentAccount,
			isDisabled: componentAccount?.relatedComponentAccounts?.some(
				(s) => s.primaryAccountId !== primaryAccount.accountId
			),
			isSelected: values[id]?.includes(componentAccount.accountId),
			isLoading: isLoading?.includes(componentAccount.accountId)
		});

		const props = {
			id,
			items: componentAccounts.map(mapComponent),
			onSelect
		};

		if (errors[id]) props.errorMessage = errors[id]?.message;

		return <RelateAccountsTable {...props} />;
	};

	const props = {
		show,
		modalsize: 'medium',
		title: labels.relateAccountsModalTitle,
		labels: {
			modalConfirmBtnLabel: labels.save,
			modalCancelBtnLabel: labels.cancelLabel,
			modalCloseBtnTitletip: labels.closeLabel
		},
		onClose: () => {
			handleReset();
			onHide();
		},
		disableClose: isLoading.length > 0,
		showFooter: false
	};

	return (
		show && (
			<Modal {...props}>
				<StyledRelateAccountsModal>
					<OutputText light>{Utility.createRichTextElement(labels.relateAccountInstructionalText)}</OutputText>
					<StyledLabel>
						{renderAccountName()}
						{renderPspIndex()}
					</StyledLabel>
					{renderTable()}
				</StyledRelateAccountsModal>
			</Modal>
		)
	);
};

RelateAccountsModal.propTypes = {};

export default RelateAccountsModal;
